import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import Button from '../../../components/Button';
import Icon from '../../../components/Icon';
import NamedItem from '../../../components/NamedItem';
import TabNavigation from '../../../components/TabNavigation';
import downloadFile from '../../../utils/downloadFile';
import formatDate from '../../../utils/formatDate';
import formatDateTime from '../../../utils/formatDateTime';
import {
  downloadKpiCsv,
  getCombinedKpiReport,
  getKpiList,
  getKpiReports,
  isReportFetchError,
  KpiInfo,
  KpiMode,
  KpiName,
  KpiReportRow,
  KpiRequest,
  PartitionBy,
  ReportFetchError,
  TimeGranularity,
} from '../../../services/reportServices';
import { useNotification } from '../../../context/notificationContext';
import ChartLegend, { LegendEntry } from './ChartLegend';
import { Card, SpinnerWrapper } from './ReportCard';
import { displayName } from './PartnerMultiSelect';
import { readDay, readEnum, withParam, writePartners } from '../urlState';

const GRANULARITIES: TimeGranularity[] = ['Daily', 'Weekly', 'Monthly'];
const PARTITIONS: PartitionBy[] = ['NoPartition', 'ByHandler'];

// Toggle order; the labels below follow it.
const KPI_MODES: KpiMode[] = ['perPartner', 'combined'];

// The default range starts three months back (and ends today).
const defaultFromDate = (): Date => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
};

// A small fixed palette; lines cycle through it by series index, dashed from
// the second time round.
const COLORS = [
  '#2563eb',
  '#dc2626',
  '#16a34a',
  '#d97706',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#65a30d',
];

// One input series: a partner's report, or the combined one. Charted as one
// line, or one per handler when partitioned by handler.
type KpiSeries = {
  // The partner's API name; null for the combined series.
  partnerName: string | null;
  computedAt: string;
  rows: KpiReportRow[];
};

// A charted line. `key` is its column in the pivoted data and the recharts
// dataKey: the partner name or 'combined', plus the handler when partitioned.
// `name` is what the legend and the tooltip show.
type ChartLine = {
  key: string;
  name: string;
};

type ChartShape = {
  data: Array<Record<string, string | number>>;
  lines: ChartLine[];
  // Sample sizes per period and line key, for the KPIs that have them.
  sampleSizes: Map<string, Record<string, number>>;
};

// Format a value for the tooltip: averages to one decimal, with their sample
// size when known; counts as they are.
const formatValue = (
  value: unknown,
  isAverage: boolean,
  sampleSize: number | undefined
): string => {
  const shown =
    isAverage && typeof value === 'number' ? value.toFixed(1) : String(value);
  return sampleSize === undefined ? shown : `${shown} (n = ${sampleSize})`;
};

// What the lines show, where that is not obvious from the chart itself.
const kindNote = (mode: KpiMode, isAverage: boolean): string | null => {
  if (mode === 'combined') {
    return isAverage
      ? 'Combined: the mean over the selected partners, weighted by sample size (n, shown in the tooltip).'
      : 'Combined: the sum over the selected partners, per period.';
  }
  return isAverage ? 'Sample sizes (n) are shown in the tooltip.' : null;
};

const loadSeries = async (
  mode: KpiMode,
  query: KpiRequest
): Promise<KpiSeries[]> => {
  if (mode === 'combined') {
    const { rows, computedAt } = await getCombinedKpiReport(query);
    return [{ partnerName: null, computedAt, rows }];
  }
  const reports = await getKpiReports(query);
  return reports.map((report) => ({
    partnerName: report.partnerName,
    computedAt: report.computedAt,
    rows: report.rows,
  }));
};

const handlerName = (partitionedBy: string): string =>
  partitionedBy === '' ? 'Unassigned' : partitionedBy;

const partnersCount = (n: number): string =>
  `${n} partner${n === 1 ? '' : 's'}`;

// The combined series is named by the size of the selection: the Partners
// control already says which partners, and their names joined do not fit a
// legend.
const combinedLabel = (selectedPartners: string[]): string =>
  selectedPartners.length === 1
    ? displayName(selectedPartners[0])
    : `${partnersCount(selectedPartners.length)} combined`;

// Pivot the tall rows into one record per period with a column per line: one
// per input series, or one per handler within it when partitioned by handler.
// Lines keep the input order; a series' handlers are sorted by name. With a
// single input series (one partner, or the combined one) a handler line is
// named by the handler alone: the partner set is shown by the Partners
// control.
const pivot = (
  inputs: KpiSeries[],
  byHandler: boolean,
  combined: string
): ChartShape => {
  const byDate = new Map<string, Record<string, string | number>>();
  const sampleSizes = new Map<string, Record<string, number>>();
  const lines: ChartLine[] = [];
  for (const input of inputs) {
    const id = input.partnerName ?? 'combined';
    const label =
      input.partnerName === null ? combined : displayName(input.partnerName);
    const lineOf = (row: KpiReportRow): ChartLine => {
      if (!byHandler) return { key: id, name: label };
      const handler = handlerName(row.partitionedBy);
      return {
        key: `${id}:${row.partitionedBy}`,
        name: inputs.length === 1 ? handler : `${label} – ${handler}`,
      };
    };
    const seen = new Map<string, ChartLine>();
    for (const r of input.rows) {
      const line = lineOf(r);
      seen.set(line.key, line);
      const record: Record<string, string | number> = byDate.get(
        r.atEndOf
      ) ?? { atEndOf: r.atEndOf };
      record[line.key] = r.value;
      byDate.set(r.atEndOf, record);
      if (r.sampleSize != null) {
        const sizes = sampleSizes.get(r.atEndOf) ?? {};
        sizes[line.key] = r.sampleSize;
        sampleSizes.set(r.atEndOf, sizes);
      }
    }
    lines.push(
      ...Array.from(seen.values()).sort((a, b) =>
        a.name < b.name ? -1 : a.name > b.name ? 1 : 0
      )
    );
  }
  const data = Array.from(byDate.values()).sort((a, b) =>
    String(a.atEndOf) < String(b.atEndOf) ? -1 : 1
  );
  return { data, lines, sampleSizes };
};

type MissingForSomePartnersProps = {
  kpiLabel: string;
  error: ReportFetchError;
  onNarrow: (partners: string[]) => void;
};

// A KPI some of the selected partners do not have (typically one that was
// never computed for them), with the offer to ask again for the ones that do.
const MissingForSomePartners = ({
  kpiLabel,
  error,
  onNarrow,
}: MissingForSomePartnersProps) => {
  const total = error.failed.length + error.succeeded.length;
  return (
    <PartialFailure>
      <p>
        {kpiLabel} is not available for {error.failed.length} of{' '}
        {partnersCount(total)} selected:{' '}
        {error.failed.map((f) => displayName(f.partnerName)).join(', ')}.
      </p>
      <details>
        <summary>Why</summary>
        <ul>
          {error.failed.map((f) => (
            <li key={f.partnerName}>
              {displayName(f.partnerName)}: {f.reason}
            </li>
          ))}
        </ul>
      </details>
      <Button
        theme='orange'
        size='small'
        onClick={() => onNarrow(error.succeeded)}
      >
        Show the {partnersCount(error.succeeded.length)} that have it
      </Button>
    </PartialFailure>
  );
};

type KpiPanelProps = {
  partnersParam: string[] | undefined;
  selectedPartners: string[];
  disabled: boolean;
};

const KpiPanel = ({
  partnersParam,
  selectedPartners,
  disabled,
}: KpiPanelProps) => {
  const [kpiList, setKpiList] = useState<KpiInfo[]>([]);
  // Every selection is a URL parameter (see ../urlState.ts), so each change
  // is a history entry and Back steps through them. A parameter that is
  // absent means the default.
  const [searchParams, setSearchParams] = useSearchParams();
  // Only a KPI the list knows counts as selected: until the list has loaded,
  // and for an unknown name, nothing is fetched. Taking the name from the
  // list also narrows the URL string to the API's KpiName union.
  const requestedKpi = searchParams.get('kpi') ?? '';
  const kpiName: KpiName | '' =
    kpiList.find((kpi) => kpi.name === requestedKpi)?.name ?? '';
  const granularity: TimeGranularity =
    readEnum(searchParams.get('granularity'), GRANULARITIES) ?? 'Weekly';
  const partitionBy: PartitionBy =
    readEnum(searchParams.get('partition'), PARTITIONS) ?? 'NoPartition';
  const fromDate: Date = readDay(searchParams.get('from')) ?? defaultFromDate();
  const toDate: Date = readDay(searchParams.get('to')) ?? new Date();
  const mode: KpiMode =
    readEnum(searchParams.get('mode'), KPI_MODES) ?? 'perPartner';
  const setParam = (key: string, value: string | undefined) =>
    setSearchParams(withParam(searchParams, key, value));
  const [series, setSeries] = useState<KpiSeries[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<Error | null>(null);
  const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const { addNotification, removeNotificationWithDelay } = useNotification();

  useEffect(() => {
    const loadKpiList = async () => {
      try {
        setKpiList(await getKpiList());
      } catch (error) {
        removeNotificationWithDelay(
          addNotification(
            `Could not fetch the KPI list: ${(error as Error).message}`,
            'alert'
          )
        );
      }
    };

    loadKpiList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedKpi = kpiList.find((kpi) => kpi.name === kpiName);
  const canPartition = selectedKpi?.handlerPartition ?? false;
  const effectivePartition: PartitionBy = canPartition
    ? partitionBy
    : 'NoPartition';
  // The range as the API takes it (yyyy-MM-dd).
  const startDay = formatDate(fromDate);
  const endDay = formatDate(toDate);
  const ready = kpiName !== '' && !disabled;

  useEffect(() => {
    if (kpiName === '' || disabled) {
      setSeries([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsFetching(true);
      setFetchError(null);
      // Handler emails repeat between KPIs, so a hidden line must not
      // carry over to the next selection.
      setHiddenLines(new Set());
      try {
        const loaded = await loadSeries(mode, {
          kpiName,
          granularity,
          partitionBy: effectivePartition,
          startDay,
          endDay,
          partners: partnersParam,
        });
        if (!cancelled) setSeries(loaded);
      } catch (error) {
        if (!cancelled) {
          setSeries([]);
          setFetchError(error as Error);
        }
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    kpiName,
    granularity,
    effectivePartition,
    startDay,
    endDay,
    mode,
    partnersParam,
    disabled,
  ]);

  // A string, so the memo below stays put across the parent's re-renders
  // even though `selectedPartners` is a fresh array each time.
  const combined = combinedLabel(selectedPartners);
  const { data, lines, sampleSizes } = useMemo(
    () => pivot(series, effectivePartition === 'ByHandler', combined),
    [series, effectivePartition, combined]
  );
  // Each line's colour, for the plot and the legend alike.
  const legend: LegendEntry[] = lines.map((line, index) => ({
    ...line,
    color: COLORS[index % COLORS.length],
    dashed: index >= COLORS.length,
  }));
  const toggleLine = (key: string) =>
    setHiddenLines((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  const isAverage = selectedKpi?.kind === 'PeriodAverage';
  const note = kindNote(mode, isAverage);
  // The shown data is only as fresh as its oldest input series. Null only
  // while nothing has been loaded yet.
  const computedAt = useMemo(() => {
    const stamps = series.map((s) => s.computedAt);
    return stamps.length === 0
      ? null
      : stamps.reduce((a, b) => (new Date(a) <= new Date(b) ? a : b));
  }, [series]);

  const handleDownloadClick = async () => {
    if (kpiName === '') return;
    setIsGenerating(true);
    try {
      const blob = await downloadKpiCsv(mode, {
        kpiName,
        granularity,
        partitionBy: effectivePartition,
        startDay,
        endDay,
        partners: partnersParam,
      });
      downloadFile(
        blob,
        `kpi_${kpiName}_${granularity}_${effectivePartition}_${mode}_${new Date().toISOString()}.csv`
      );
    } catch (error) {
      removeNotificationWithDelay(
        addNotification(
          `Could not generate the KPI report: ${(error as Error).message}`,
          'alert'
        ),
        15000
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <h3>KPI</h3>
      <Controls>
        <NamedItem>
          <label>KPI</label>
          <select
            value={kpiName}
            onChange={(event) => setParam('kpi', event.target.value)}
          >
            <option value=''>-- Select a KPI --</option>
            {kpiList.map((kpi) => (
              <option key={kpi.name} value={kpi.name}>
                {kpi.label}
              </option>
            ))}
          </select>
        </NamedItem>
        <NamedItem>
          <label>Time aggregation</label>
          <select
            value={granularity}
            onChange={(event) => setParam('granularity', event.target.value)}
          >
            {GRANULARITIES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </NamedItem>
        <NamedItem>
          <label>Partition by</label>
          <select
            value={effectivePartition}
            onChange={(event) => setParam('partition', event.target.value)}
            disabled={!canPartition}
          >
            <option value='NoPartition'>None (total)</option>
            <option value='ByHandler'>Claim handler</option>
          </select>
        </NamedItem>
        <NamedItem>
          <label>From date</label>
          <DatePicker
            className='default'
            locale='sv'
            dateFormat='yyyy-MM-dd'
            maxDate={toDate}
            selected={fromDate}
            onChange={(date: Date | null) =>
              setParam('from', date === null ? undefined : formatDate(date))
            }
          />
        </NamedItem>
        <NamedItem>
          <label>To date (inclusive)</label>
          <DatePicker
            className='default'
            locale='sv'
            dateFormat='yyyy-MM-dd'
            selected={toDate}
            minDate={fromDate}
            maxDate={new Date()}
            onChange={(date: Date | null) =>
              setParam('to', date === null ? undefined : formatDate(date))
            }
          />
        </NamedItem>
        <NamedItem>
          <label>Partners</label>
          <TabNavigation
            size='small'
            selectedIndex={KPI_MODES.indexOf(mode)}
            onClick={(index) => setParam('mode', KPI_MODES[index])}
            items={[
              { content: <>Per partner</> },
              { content: <>Combined</> },
            ]}
          />
        </NamedItem>
      </Controls>

      {selectedKpi !== undefined && (
        <KindNote>{selectedKpi.description}</KindNote>
      )}
      {note !== null && <KindNote>{note}</KindNote>}

      <ChartArea>
        {!ready ? (
          <Placeholder>
            {disabled
              ? 'Select at least one partner.'
              : 'Select a KPI and time aggregation to view it.'}
          </Placeholder>
        ) : isFetching ? (
          <Placeholder>Loading…</Placeholder>
        ) : fetchError !== null ? (
          <FetchError>
            {isReportFetchError(fetchError) &&
            fetchError.succeeded.length > 0 ? (
              <MissingForSomePartners
                kpiLabel={selectedKpi?.label ?? kpiName}
                error={fetchError}
                onNarrow={(partners) =>
                  setSearchParams(writePartners(searchParams, partners))
                }
              />
            ) : (
              fetchError.message
            )}
          </FetchError>
        ) : data.length === 0 ? (
          <Placeholder>No data for this selection.</Placeholder>
        ) : (
          <>
            <ResponsiveContainer width='100%' height={360}>
              <LineChart
                data={data}
                margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
              >
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='atEndOf' />
                <YAxis allowDecimals={isAverage} />
                <Tooltip
                  formatter={(value, name, item) =>
                    formatValue(
                      value,
                      isAverage,
                      sampleSizes.get(String(item.payload?.atEndOf))?.[
                        String(item.dataKey ?? name)
                      ]
                    )
                  }
                />
                {legend.map((line) => (
                  <Line
                    key={line.key}
                    type='monotone'
                    dataKey={line.key}
                    name={line.name}
                    stroke={line.color}
                    strokeDasharray={line.dashed ? '6 3' : undefined}
                    hide={hiddenLines.has(line.key)}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
            <ChartLegend
              entries={legend}
              hidden={hiddenLines}
              onToggle={toggleLine}
            />
          </>
        )}
      </ChartArea>

      {computedAt !== null && data.length > 0 && (
        <KindNote>Data as of {formatDateTime(computedAt)}</KindNote>
      )}

      <ButtonWrapper>
        <Button
          theme='orange'
          size='small'
          onClick={handleDownloadClick}
          disabled={!ready || isGenerating}
        >
          <SpinnerWrapper isSpinning={isGenerating}>
            <Icon iconName='faArrowsRotate' />
          </SpinnerWrapper>{' '}
          {isGenerating ? 'Generating…' : 'Download CSV'}
        </Button>
      </ButtonWrapper>
    </Card>
  );
};

export default KpiPanel;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-top: 0.5rem;

  input {
    border-color: #c8ccd0;
  }
`;

const KindNote = styled.p`
  color: #7b7d8c;
  font-size: 0.85rem;
  margin: 0;
`;

const ChartArea = styled.div`
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const Placeholder = styled.div`
  color: #7b7d8c;
`;

const FetchError = styled.div`
  color: #c53030;
  white-space: pre-wrap;
  max-width: 40rem;
`;

const PartialFailure = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;

  p,
  ul {
    margin: 0;
  }

  details {
    color: #7b7d8c;
    font-size: 0.85rem;
  }
`;

const ButtonWrapper = styled.div`
  align-self: flex-end;
`;
